import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BusinessIcon from "@mui/icons-material/Business";


// Import Redux actions
import {
  getBudgetRecords,
  getBudgetCharts,
  getOrgBudgets,
} from "../../store/spendCategorization/budgetSlice";
import BudgetCharts from "./components/BudgetCharts";

const GroupBasedReport = () => {
  const { departmentName, startDate, endDate, accountNumbers } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [budgetData, setBudgetData] = useState(null);
  const [currentBudgetId, setCurrentBudgetId] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Get budget records from Redux store
  const {
    budgetRecords,
    isFetchingBudgetRecords,
    budgetCharts,
    isFetchingBudgetCharts,
    fetchBudgetChartsError,
    orgBudgets,
    isFetchingOrgBudgets,
  } = useSelector((state) => state.budget);
  console.log("🚀 ~ GroupBasedReport ~ budgetCharts:", budgetCharts);

  // Decode the URL parameters
  const decodedDepartmentName = decodeURIComponent(departmentName || "");
  const decodedStartDate = decodeURIComponent(startDate || "");
  const decodedEndDate = decodeURIComponent(endDate || "");
  const decodedAccountNumbers = decodeURIComponent(accountNumbers || "");

  console.log("🔍 GroupBasedReport: URL Parameters:", {
    departmentName: decodedDepartmentName,
    startDate: decodedStartDate,
    endDate: decodedEndDate,
    accountNumbers: decodedAccountNumbers,
  });

  useEffect(() => {
    // Always fetch data if it's not available - this makes the component robust
    // for navigation from any page (1->2, 3->2, direct URL, etc.)
    if (!budgetRecords || budgetRecords.length === 0) {
      console.log(
        "🔄 GroupBasedReport: No budget records found, fetching data..."
      );
      dispatch(getBudgetRecords());
      setLastFetchTime(new Date());
    }

    // Fetch organization budgets if not available
    if (!orgBudgets || orgBudgets.length === 0) {
      console.log(
        "🔄 GroupBasedReport: No org budgets found, fetching data..."
      );
      dispatch(getOrgBudgets());
      setLastFetchTime(new Date());
    }
  }, [dispatch, budgetRecords, orgBudgets]);

  useEffect(() => {
    if (!isFetchingBudgetRecords && budgetRecords && budgetRecords.length > 0) {
      console.log(
        "🔄 GroupBasedReport: Processing budget records...",
        budgetRecords.length
      );

      // Find the specific budget record based on URL parameters
      const matchingRecord = budgetRecords.find(
        (record) =>
          record.resource_group_name === decodedDepartmentName &&
          record.start_date === decodedStartDate &&
          record.end_date === decodedEndDate
      );

      if (matchingRecord) {
        console.log(
          "✅ GroupBasedReport: Found matching record:",
          matchingRecord
        );

        // Transform the record data to match our component's expected format
        const transformedData = {
          budgetAmount: matchingRecord.amount || 0,
          currentActualSpend: parseFloat(matchingRecord.total_usage) || 0,
          forecastedSpend: parseFloat(matchingRecord.usage_forecast) || 0,
          contactName: matchingRecord.admin_name || "Not provided",
          contactEmail: matchingRecord.admin_email || "Not provided",
          contactPhone: matchingRecord.admin_phone || "Not provided",
          currency: matchingRecord.currency || "USD",
          // Additional fields from the actual data
          actualSpendPct: matchingRecord.actual_spend_pct || 0,
          actualDiffPct: matchingRecord.actual_difference_pct || 0,
          forecastedPct: matchingRecord.forecasted_spend_pct || 0,
          resourceGroupName: matchingRecord.resource_group_name,
          startDate: matchingRecord.start_date,
          endDate: matchingRecord.end_date,
        };

        // Calculate budget surplus/deficit using forecast_overage
        // forecast_overage = amount - usage_forecast
        // Positive forecast_overage = surplus, Negative forecast_overage = deficit
        const forecastOverage =
          transformedData.budgetAmount - transformedData.forecastedSpend;
        const isSurplus = forecastOverage > 0;
        const surplusDeficitAmount = Math.abs(forecastOverage);
        const surplusDeficitPct =
          (forecastOverage / transformedData.budgetAmount) * 100;

        transformedData.isSurplus = isSurplus;
        transformedData.surplusDeficitAmount = surplusDeficitAmount;
        transformedData.surplusDeficitPct = surplusDeficitPct;

        setBudgetData(transformedData);

        // Fetch budget charts data for this specific budget (only if budget ID changed)
        if (currentBudgetId !== matchingRecord.budget_id) {
          console.log(
            "🔄 GroupBasedReport: Fetching budget charts for budget_id =",
            matchingRecord.budget_id
          );
          dispatch(getBudgetCharts(matchingRecord.budget_id));
          
          setCurrentBudgetId(matchingRecord.budget_id);
        }
      }
    }
  }, [
    isFetchingBudgetRecords,
    decodedStartDate,
    decodedEndDate,
    decodedDepartmentName,
    dispatch,
  ]);

  // Get the budget allocated to this specific group (from the budget record)
  const groupBudget = budgetData?.budgetAmount || 0;

  const handleBack = () => {
    history.goBack();
  };

  const handleViewDepartmentDashboard = () => {
    const encodedDepartmentName = encodeURIComponent(decodedDepartmentName);
    const encodedStartDate = encodeURIComponent(decodedStartDate);
    const encodedEndDate = encodeURIComponent(decodedEndDate);
    const encodedAccountNumbers = encodeURIComponent(decodedAccountNumbers);
    history.push(
      `/department-dashboard/${encodedDepartmentName}/${encodedStartDate}/${encodedEndDate}/${encodedAccountNumbers}`
    );
  };



  // Debug budget charts data
  useEffect(() => {
    if (budgetCharts) {
      console.log(
        "📊 GroupBasedReport: Budget charts data received:",
        budgetCharts
      );
    }
  }, [budgetCharts]);

  useEffect(() => {
    if (fetchBudgetChartsError) {
      console.error(
        "❌ GroupBasedReport: Budget charts error:",
        fetchBudgetChartsError
      );
    }
  }, [fetchBudgetChartsError]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: budgetData?.currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format the last fetch time for display
  const formatLastFetchTime = () => {
    if (!lastFetchTime) return null;
    
    return lastFetchTime.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getStatusColor = (percentage, type = "spend") => {
    if (type === "spend") {
      // Match the exact thresholds from index.js Usage column
      if (percentage >= 90) return "#dc2626"; // Red (≥ 90%)
      if (percentage >= 70) return "#f59e0b"; // Amber (70-90%)
      return "#059669"; // Green (< 70%)
    } else if (type === "forecast") {
      // Match the exact thresholds from index.js Forecast column
      if (percentage >= 110) return "#dc2626"; // Red (≥ 110%)
      if (percentage >= 90) return "#f59e0b"; // Amber (90-110%)
      return "#059669"; // Green (< 90%)
    } else if (type === "surplus") {
      // For surplus/deficit based on forecast_overage calculation
      if (percentage < -10) return "#dc2626"; // Red (deficit > 10% of budget)
      if (percentage >= -10 && percentage <= 10) return "#f59e0b"; // Amber (within ±10% of budget)
      return "#059669"; // Green (surplus > 10% of budget)
    } else {
      // Legacy surplus/deficit logic
      if (percentage > 10) return "#dc2626"; // Red (over budget by >10%)
      if (percentage >= -10 && percentage <= 10) return "#f59e0b"; // Amber (within ±10% of budget)
      return "#059669"; // Green (surplus > 10% or deficit < -10%)
    }
  };

  const getStatusIcon = (percentage, type = "spend") => {
    if (type === "spend") {
      if (percentage >= 90)
        return <TrendingUpIcon sx={{ color: "#dc2626", fontSize: 14 }} />;
      if (percentage >= 70)
        return <TrendingUpIcon sx={{ color: "#f59e0b", fontSize: 14 }} />;
      return <TrendingDownIcon sx={{ color: "#059669", fontSize: 14 }} />;
    } else if (type === "forecast") {
      if (percentage >= 110)
        return <TrendingUpIcon sx={{ color: "#dc2626", fontSize: 14 }} />;
      if (percentage >= 90)
        return <TrendingUpIcon sx={{ color: "#f59e0b", fontSize: 14 }} />;
      return <TrendingDownIcon sx={{ color: "#059669", fontSize: 14 }} />;
    } else if (type === "surplus") {
      // For surplus/deficit based on forecast_overage calculation
      if (percentage < -10)
        return <TrendingUpIcon sx={{ color: "#dc2626", fontSize: 14 }} />; // Deficit (concerning)
      if (percentage >= -10 && percentage <= 10)
        return <TrendingUpIcon sx={{ color: "#f59e0b", fontSize: 14 }} />; // Close to target
      return <TrendingDownIcon sx={{ color: "#059669", fontSize: 14 }} />; // Surplus (good)
    } else {
      // Legacy surplus/deficit logic
      if (percentage > 10)
        return <TrendingUpIcon sx={{ color: "#dc2626", fontSize: 14 }} />;
      if (percentage >= -10 && percentage <= 10)
        return <TrendingUpIcon sx={{ color: "#f59e0b", fontSize: 14 }} />;
      return <TrendingDownIcon sx={{ color: "#059669", fontSize: 14 }} />;
    }
  };

  // Loading state with spinner
  if (isFetchingBudgetRecords) {
    return (
      <Box
        sx={{
          padding: "1rem",
          mb: "2rem",
          backgroundColor: "#F9F9F9",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{
            color: "#0288d1",
            mb: 1,
          }}
        />
        <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 500 }}>
          Loading budget data...
        </Typography>
      </Box>
    );
  }

  // No data state
  if (!budgetData) {
    return (
      <Box
        sx={{
          padding: "1rem",
          mb: "2rem",
          backgroundColor: "#F9F9F9",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            backgroundColor: "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
            border: "2px solid #fecaca",
          }}
        >
          <BusinessIcon sx={{ color: "#dc2626", fontSize: 30 }} />
        </Box>
        <Typography
          variant="h6"
          sx={{ color: "#dc2626", fontWeight: 600, mb: 1 }}
        >
          No Budget Data Found
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#64748b", textAlign: "center", mb: 2 }}
        >
          We couldn't find budget data for{" "}
          <strong>{decodedDepartmentName}</strong>
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            borderColor: "#d1d5db",
            color: "#374151",
            backgroundColor: "#ffffff",
            fontWeight: 600,
            fontSize: "0.875rem",
            padding: "8px 16px",
            borderRadius: "6px",
            textTransform: "none",
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

    const maxValue = Math.max(budgetData.budgetAmount, budgetData.currentActualSpend, budgetData.forecastedSpend);

  return (
    <Box
      sx={{
        padding: "1rem",
        mb: "2rem",
        backgroundColor: "#F9F9F9",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Compact Header */}
      <Box sx={{ mb: 2 }}>
        <Paper
          sx={{
            borderRadius: "8px",
            p: 2,
            border: "1px solid #e2e8f0",
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                mb: 0.5,
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
                lineHeight: 1.2,
              }}
            >
              Group Based Report - {budgetData.resourceGroupName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              {budgetData.startDate} to {budgetData.endDate}
            </Typography>
            {formatLastFetchTime() && (
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  fontStyle: "italic",
                }}
              >
                Data fetched on {formatLastFetchTime()}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              variant="contained"
              onClick={handleViewDepartmentDashboard}
              size="small"
              sx={{
                backgroundColor: "#0288d1",
                color: "white",
                fontWeight: 600,
                fontSize: "0.75rem",
                padding: "6px 12px",
                borderRadius: "6px",
                textTransform: "none",
                boxShadow: "0 2px 4px -1px rgba(2, 136, 209, 0.2)",
                "&:hover": {
                  backgroundColor: "#0277bd",
                  boxShadow: "0 4px 6px -1px rgba(2, 136, 209, 0.3)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              View Dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              size="small"
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                backgroundColor: "#ffffff",
                fontWeight: 600,
                fontSize: "0.75rem",
                padding: "6px 12px",
                borderRadius: "6px",
                textTransform: "none",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Compact Budget Details Section - 2 lines only */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "6px",
              backgroundColor: "#e0f2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #0288d1",
            }}
          >
            <BusinessIcon sx={{ color: "#0288d1", fontSize: 14 }} />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "0.875rem",
            }}
          >
            Budget Details
          </Typography>
        </Box>

        {/* Single line with all details - responsive wrapping */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 1.5, md: 2 },
            alignItems: "center",
            justifyContent: { xs: "flex-start", sm: "space-between" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.7rem" }}
            >
              Budget:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#059669", fontWeight: 700, fontSize: "0.8rem" }}
            >
              {formatCurrency(budgetData.budgetAmount)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.7rem" }}
            >
              Contact:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#1e293b", fontWeight: 600, fontSize: "0.8rem" }}
            >
              {budgetData.contactName}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.7rem" }}
            >
              Email:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#1e293b", fontWeight: 600, fontSize: "0.8rem" }}
            >
              {budgetData.contactEmail}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.7rem" }}
            >
              Phone:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#1e293b", fontWeight: 600, fontSize: "0.8rem" }}
            >
              {budgetData.contactPhone}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.7rem" }}
            >
              Period:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#1e293b", fontWeight: 600, fontSize: "0.8rem" }}
            >
              {budgetData.startDate} to {budgetData.endDate}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Charts Section - Two Charts Side by Side */}
      <Grid container spacing={2}>
        {/* Financial Summary Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              boxShadow:
                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
              height: "450px",
              minHeight: "450px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                mb: 2,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              Financial Summary
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2 }, flex: 1, justifyContent: "space-between" }}>
              {/* Budget Amount */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1e293b",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Budget Amount: {formatCurrency(budgetData.budgetAmount)}
                  </Typography>
                  <Chip
                    label="100%"
                    size="small"
                    sx={{
                      backgroundColor: "#059669",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.625rem",
                      height: "20px",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 12, sm: 14, md: 16 },
                    backgroundColor: "#f0fdf4",
                    borderRadius: { xs: "6px", sm: "7px", md: "8px" },
                    border: "1px solid #059669",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#059669",
                      borderRadius: "7px",
                    }}
                  />
                </Box>
              </Box>

              {/* Current Actual Spend */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1e293b",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Current Actual Spend:{" "}
                    {formatCurrency(budgetData.currentActualSpend)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={`${budgetData.actualSpendPct.toFixed(1)}%`}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(
                          budgetData.actualSpendPct,
                          "spend"
                        ),
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.625rem",
                        height: "20px",
                      }}
                    />
                    {getStatusIcon(budgetData.actualSpendPct, "spend")}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 12, sm: 14, md: 16 },
                                      backgroundColor:
                    getStatusColor(
                      budgetData.actualSpendPct,
                      "spend"
                    ) === "#dc2626"
                      ? "#fef2f2"
                      : getStatusColor(
                          budgetData.actualSpendPct,
                          "spend"
                        ) === "#f59e0b"
                      ? "#fffbeb"
                      : "#f0fdf4",
                  borderRadius: { xs: "6px", sm: "7px", md: "8px" },
                  border: `1px solid ${getStatusColor(
                    budgetData.actualSpendPct,
                    "spend"
                  )}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(
                        (budgetData.currentActualSpend / maxValue) * 100,
                        100
                      )}%`,
                      height: "100%",
                      backgroundColor: getStatusColor(
                        budgetData.actualSpendPct,
                        "spend"
                      ),
                      borderRadius: { xs: "5px", sm: "6px", md: "7px" },
                    }}
                  />
                </Box>
              </Box>

              {/* Forecasted Spend */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1e293b",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Forecasted Spend:{" "}
                    {formatCurrency(budgetData.forecastedSpend)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={`${budgetData.forecastedPct.toFixed(1)}%`}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(
                          budgetData.forecastedPct,
                          "forecast"
                        ),
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.625rem",
                        height: "20px",
                      }}
                    />
                    {getStatusIcon(budgetData.forecastedPct, "forecast")}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 12, sm: 14, md: 16 },
                                      backgroundColor:
                    getStatusColor(
                      budgetData.forecastedPct,
                      "forecast"
                    ) === "#dc2626"
                      ? "#fef2f2"
                      : getStatusColor(
                          budgetData.forecastedPct,
                          "forecast"
                        ) === "#f59e0b"
                      ? "#fffbeb"
                      : "#f0fdf4",
                  borderRadius: { xs: "6px", sm: "7px", md: "8px" },
                  border: `1px solid ${getStatusColor(
                    budgetData.forecastedPct,
                    "forecast"
                  )}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(
                        (budgetData.forecastedSpend / maxValue) * 100,
                        100
                      )}%`,
                      height: "100%",
                      backgroundColor: getStatusColor(
                        budgetData.forecastedPct,
                        "forecast"
                      ),
                      borderRadius: { xs: "5px", sm: "6px", md: "7px" },
                    }}
                  />
                </Box>
              </Box>

              {/* Budget Surplus/Deficit */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1e293b",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {budgetData.isSurplus ? "Budget Surplus" : "Budget Deficit"}
                    : {formatCurrency(budgetData.surplusDeficitAmount)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={`${budgetData.surplusDeficitPct.toFixed(1)}%`}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(
                          budgetData.surplusDeficitPct,
                          "surplus"
                        ),
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.625rem",
                        height: "20px",
                      }}
                    />
                    {getStatusIcon(
                      budgetData.surplusDeficitPct,
                      "surplus"
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 12, sm: 14, md: 16 },
                                      backgroundColor:
                    getStatusColor(
                      budgetData.surplusDeficitPct,
                      "surplus"
                    ) === "#dc2626"
                      ? "#fef2f2"
                      : getStatusColor(
                          budgetData.surplusDeficitPct,
                          "surplus"
                        ) === "#f59e0b"
                      ? "#fffbeb"
                      : "#f0fdf4",
                  borderRadius: { xs: "6px", sm: "7px", md: "8px" },
                  border: `1px solid ${getStatusColor(
                    budgetData.surplusDeficitPct,
                    "surplus"
                  )}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(
                        (Math.abs(budgetData.surplusDeficitPct) / 100) * 100,
                        100
                      )}%`,
                      height: "100%",
                      backgroundColor: getStatusColor(
                        budgetData.surplusDeficitPct,
                        "surplus"
                      ),
                      borderRadius: { xs: "5px", sm: "6px", md: "7px" },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Monthly Budget Trends Chart */}
        <Grid item xs={12} md={6}>
          {budgetCharts && !isFetchingBudgetCharts ? (
            <BudgetCharts
              data={budgetCharts}
              currency={budgetData?.currency || "USD"}
              totalBudget={groupBudget}
              startDate={budgetData?.startDate}
              endDate={budgetData?.endDate}
            />
          ) : isFetchingBudgetCharts ? (
            <Paper
              sx={{
                p: 2.5,
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                height: "450px",
                minHeight: "450px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Loading budget trends...
                </Typography>
              </Box>
            </Paper>
          ) : fetchBudgetChartsError ? (
            <Paper
              sx={{
                p: 2.5,
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                height: "400px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#dc2626", textAlign: "center" }}
              >
                Error loading budget trends: {fetchBudgetChartsError}
              </Typography>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 2.5,
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                height: "450px",
                minHeight: "450px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#64748b", textAlign: "center" }}
              >
                No budget trends data available
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>


    </Box>
  );
};

export default GroupBasedReport;
